import {
  GoogleMap,
  Marker as GoogleMapsMarker,
  InfoBox,
  Polyline,
  type PolylineProps,
} from '@react-google-maps/api'
import { useEffect, useState } from 'react'

import { getMapRoutes } from '../../../use-cases/getMapRoutes'
import {
  DEFAULT_ALTERNATIVE_ROUTE_COLOR,
  DEFAULT_ALTERNATIVE_ROUTE_WEIGHT,
  DEFAULT_PRIMARY_ROUTE_WEIGHT,
  defaultMapContainerStyle,
  defaultMapStyles,
} from './Map.styles'
import { MarkerTooltip } from './MarkerTooltip'

const defaultCenter = { lat: 33.749, lng: -84.388 }

const DEFAULT_ZOOM = 8

export type Coordinate = {
  lat: number
  lng: number
}

export type Marker = {
  icon: {
    url: string
    scaledSize?: {
      width: number
      height: number
    }
    anchor?: {
      x: number
      y: number
    }
  }
  coordinate: Coordinate
}

export type MapStyle = {
  elementType?: string | null
  featureType?: string | null
  stylers: object[]
}

export type InfoMarker = {
  coordinate: Coordinate
  children: React.ReactNode
  style?: React.CSSProperties
  offset?: {
    x: number
    y: number
  }
}

export type TooltipMarker = {
  marker: Marker
  infoMarker: InfoMarker
}

export function getMapOptions(enableZoomControl: boolean, mapStyle: MapStyle[]) {
  return {
    fullscreenControl: false,
    gestureHandling: enableZoomControl ? 'cooperative' : 'none',
    keyboardShortcuts: false,
    mapTypeControl: false,
    streetViewControl: false,
    styles: mapStyle,
    zoomControl: enableZoomControl,
  }
}

export function getInfoBoxOptions(markerInfo: InfoMarker) {
  const {
    coordinate: { lat, lng },
    offset = {
      x: -40,
      y: -80,
    },
    style,
  } = markerInfo

  return {
    boxStyle: style,
    alignBottom: true,
    closeBoxURL: '',
    pixelOffset: new google.maps.Size(offset.x, offset.y),
    position: new google.maps.LatLng(lat, lng),
  }
}

export function getMarkerIcon({ icon }: Marker) {
  return {
    url: icon.url,
    anchor: icon.anchor ? new google.maps.Point(icon.anchor.x, icon.anchor.y) : null,
    scaledSize: icon.scaledSize
      ? new google.maps.Size(icon.scaledSize.width, icon.scaledSize.height)
      : null,
  }
}

export type MapProps = {
  alternativeRouteColor?: string
  alternativeRouteWeight?: number
  center?: Coordinate
  coordinates: Coordinate[]
  enableZoomControl?: boolean
  infoMarkers?: InfoMarker[]
  mapContainerStyle?: React.CSSProperties
  mapStyle?: MapStyle[]
  markers: Marker[]
  primaryRouteColor?: string
  primaryRouteWeight?: number
  showAlternativeRoutes?: boolean
  tooltipMarkers?: TooltipMarker[]
  zoom?: number
}

export function Map({
  alternativeRouteColor = DEFAULT_ALTERNATIVE_ROUTE_COLOR,
  alternativeRouteWeight = DEFAULT_ALTERNATIVE_ROUTE_WEIGHT,
  center = defaultCenter,
  coordinates,
  enableZoomControl = true,
  infoMarkers,
  mapContainerStyle = defaultMapContainerStyle,
  mapStyle = defaultMapStyles,
  markers,
  primaryRouteColor = DEFAULT_ALTERNATIVE_ROUTE_COLOR,
  primaryRouteWeight = DEFAULT_PRIMARY_ROUTE_WEIGHT,
  showAlternativeRoutes = false,
  tooltipMarkers,
  zoom = DEFAULT_ZOOM,
}: MapProps) {
  const [polylines, setPolylines] = useState<PolylineProps[]>([])

  useEffect(() => {
    async function get() {
      const res = await getMapRoutes({
        alternativeRouteColor,
        alternativeRouteWeight,
        coordinates,
        primaryRouteColor,
        primaryRouteWeight,
        showAlternativeRoutes,
      })
      setPolylines(res)
    }
    get()
  }, [
    alternativeRouteColor,
    alternativeRouteWeight,
    coordinates,
    primaryRouteColor,
    primaryRouteWeight,
    showAlternativeRoutes,
  ])

  function onMapLoaded(map: google.maps.Map) {
    const bounds = new google.maps.LatLngBounds()
    markers.forEach(({ coordinate: { lat, lng } }) => {
      bounds.extend(new google.maps.LatLng(lat, lng))
    })
    map.fitBounds(bounds, 80)
  }

  return (
    <GoogleMap
      center={center}
      mapContainerStyle={mapContainerStyle}
      onLoad={onMapLoaded}
      options={getMapOptions(enableZoomControl, mapStyle)}
      zoom={zoom}
    >
      {markers.map((marker, index) => (
        <GoogleMapsMarker
          position={marker.coordinate}
          icon={getMarkerIcon(marker)}
          key={`marker-${index}`}
        />
      ))}
      {infoMarkers?.map((info, index) => (
        <InfoBox options={getInfoBoxOptions(info)} key={`infobox-${index}`}>
          {info.children}
        </InfoBox>
      ))}
      {polylines.map((polyline, index) => (
        <Polyline {...polyline} key={`polyline-${index}`} />
      ))}
      {tooltipMarkers?.map((tooltipMarker, index) => (
        <MarkerTooltip {...tooltipMarker} key={`marker-${index}`} />
      ))}
    </GoogleMap>
  )
}
